import { ChangeDetectionStrategy, Component, HostBinding, inject, OnInit, viewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SortingDirection } from '@infragistics/igniteui-angular';

import { BaseSubResource } from '../../../core/abstractions/base.sub-resource';
import { ModelTypes } from '../../../core/utils/mappers/model.types';
import { TransactionSourceEnum } from '../../../models/enums/transaction-source.enum';
import { ListTransactionModel } from '../../../models/list-transaction.model';
import { DictionaryType } from '../../../models/types/dictionary.type';
import { TransactionsResource } from '../../../services/resources/transactions.resource';
import { GridColumnConfig } from '../../paged-grid/models/grid-column.config';
import { GridDateColumnConfig } from '../../paged-grid/models/grid-date-column.config';
import { GridNumberColumnConfig } from '../../paged-grid/models/grid-number-column.config';
import { GridConfig } from '../../paged-grid/models/grid.config';
import { PagedGridComponent } from '../../paged-grid/paged-grid.component';

@Component({
    selector: 'sl-transactions-table',
    templateUrl: './transactions-table.component.html',
    styleUrls: ['./transactions-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TransactionsTableComponent extends BaseSubResource implements OnInit {
    @HostBinding('class') wrapperClass = 'fh-table';

    pagedGrid = viewChild.required('grid', { read: PagedGridComponent<ListTransactionModel> });

    gridConfig: GridConfig<ListTransactionModel>;

    private readonly transactionsResource = inject(TransactionsResource);
    private readonly activatedRoute = inject(ActivatedRoute);

    ngOnInit(): void {
        this.pagedGrid().grid().sortingExpressions = [{ fieldName: 'id', dir: SortingDirection.Desc }];

        const pathItems = this.activatedRoute.snapshot.pathFromRoot.map(p => (p?.url.length > 0 ? p?.url[0].path : '')).filter(p => !!p);

        if (pathItems.includes('traders')) {
            this.createConfig(TransactionSourceEnum.Traders, +pathItems[1]);
            return;
        }

        if (pathItems.includes('shareholders')) {
            this.createConfig(TransactionSourceEnum.Shareholders, +pathItems[1]);
            return;
        }
    }

    private createConfig(source: TransactionSourceEnum, id: number): void {
        const columns: GridColumnConfig[] = [
            new GridNumberColumnConfig({ fieldName: 'id', header: 'General.Id' }),
            new GridColumnConfig({ fieldName: 'transactionTypeName', header: 'Transactions.Type' }),
            new GridColumnConfig({ fieldName: 'outgoingName', header: 'Transactions.OutgoingName' }),
            new GridColumnConfig({ fieldName: 'incomingName', header: 'Transactions.IncomingName' }),
            new GridNumberColumnConfig({ fieldName: 'numberOfShares', header: 'Transactions.NumberOfShares' })
        ];
        const extraProperties: DictionaryType<any> = {};
        let fieldName = '';

        if (source === TransactionSourceEnum.Shareholders) {
            fieldName = 'shareholderId';
            columns.push(
                new GridNumberColumnConfig({
                    fieldName: 'registeredShareBalance',
                    header: 'Transactions.RegisteredShareBalance'
                })
            );
        } else if (source === TransactionSourceEnum.Traders) {
            fieldName = 'traderId';
            columns.push(
                new GridNumberColumnConfig({
                    fieldName: 'dispoShareBalance',
                    header: 'Transactions.DispoShareBalance'
                })
            );
        }

        extraProperties[fieldName] = id;

        columns.push(
            ...[
                new GridDateColumnConfig({ fieldName: 'valueDate', header: 'General.Date' }),
                new GridDateColumnConfig({ fieldName: 'executedDate', header: 'Transactions.Pending' })
            ]
        );

        this.gridConfig = new GridConfig<ListTransactionModel>({
            name: 'transactions',
            typeName: ModelTypes.transactions,
            resource: this.transactionsResource,
            pageSize: 10,
            hasViewButton: true,
            columns,
            extraProperties
        });
    }
}
